import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaClock, FaMap, FaMapMarked, FaMapMarkedAlt, FaRegClock, FaRoad, FaServer, FaStar, FaUsers } from 'react-icons/fa';

const Events = () => {
  const [eventsData, setEventsEvents] = useState([]);
  const { t } = useTranslation();
  const eventsRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (eventsRef.current) observer.observe(eventsRef.current);

    return () => observer.disconnect();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat('ru', { year: 'numeric', month: '2-digit', day: '2-digit' });
    return formatter.format(date);
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const cachedData = localStorage.getItem('eventsData');
        const cachedTimestamp = localStorage.getItem('eventsDataTimestamp');
  
        const isCacheValid = cachedTimestamp && (new Date().getTime() - new Date(parseInt(cachedTimestamp)).getTime()) < 1 * 3600 * 1000;
  
        if (cachedData && isCacheValid) {
          const parsedData = JSON.parse(cachedData);
          setEventsEvents(parsedData);
        } else {
          const response = await fetch('https://hub.mileageriot.com/api/events');
          const data = await response.json();
          const sortedEvents = data;
          setEventsEvents(sortedEvents);
          localStorage.setItem('eventsData', JSON.stringify(sortedEvents));
          localStorage.setItem('eventsDataTimestamp', new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching Events data:', error);
      }
    };
  
    fetchEvents();
  }, []);

  return (
    <section className="events-modern page-events" ref={eventsRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaStar className="me-2" />
            <span>{t('events.badge')}</span>
          </div>
          <h2 className="section-title text-glow text-white">
            {t('events.title')}
          </h2>
          <div className="section-decoration mx-auto"></div>
          <p className="text-white-50 mt-3 section-subtitle">
            {t('events.subtitle')}
          </p>
        </div>

        <div className='row mt-5 fade-in-up stagger-2'>
          {Array.isArray(eventsData) ? (
            <>
              {eventsData.map((eventsItem, index) => (
                <div key={index} className="col-md-4 mb-4 col-12 fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <Link to={`/events/${eventsItem.id}`} style={{ textDecoration: 'none' }}>
                    <div className="card glass-container event-card-modern">
                      <div className="event-image-container">
                        <img 
                          className="img-fluid rounded event-image" 
                          src={eventsItem.banner ?? 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'} 
                          alt={eventsItem.title || 'Event Banner'}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
                          }}
                        />
                        <div className="event-overlay">
                          <div className="event-date-badge">
                            <FaCalendarAlt className="me-2" />
                            {formatDate(eventsItem.date)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4">
                        <h5 className="text-white event-title">{eventsItem.title}</h5>
                        <div className="event-divider"></div>
                        
                        <div className="event-details">
                          <div className="event-detail-item">
                            <FaClock className='text-primary me-2'/>
                            <span className="detail-label">{t('events.eventDetails.parkingTime')}:</span>
                            <span className='text-primary detail-value'>{eventsItem.meetup_time} UTC</span>
                          </div>
                          
                          <div className="event-detail-item">
                            <FaRegClock className='text-primary me-2'/>
                            <span className="detail-label">{t('events.eventDetails.departureTime')}:</span>
                            <span className='text-primary detail-value'>{eventsItem.departure_time} UTC</span>
                          </div>
                          
                          <div className="event-detail-item">
                            <FaMapMarkedAlt className='text-primary me-2'/>
                            <span className="detail-label">{t('events.eventDetails.startLocation')}:</span>
                            <span className='text-primary detail-value'>{eventsItem.start_location}</span>
                          </div>
                          
                          <div className="event-detail-item">
                            <FaMapMarked className='text-primary me-2'/>
                            <span className="detail-label">{t('events.eventDetails.endLocation')}:</span>
                            <span className='text-primary detail-value'>{eventsItem.end_location}</span>
                          </div>
                          
                          <div className="event-detail-item">
                            <FaMap className='text-primary me-2'/>
                            <span className="detail-label">{t('events.eventDetails.dlc')}:</span>
                            <span className='text-primary detail-value'>{eventsItem.dlc}</span>
                          </div>
                          
                          <div className="event-detail-item">
                            <FaServer className='text-primary me-2'/>
                            <span className="detail-label">{t('events.eventDetails.server')}:</span>
                            <span className='text-primary detail-value'>{eventsItem.server}</span>
                          </div>
                          
                          <div className="event-detail-item">
                            <FaRoad className='text-primary me-2'/>
                            <span className="detail-label">{t('events.eventDetails.distance')}:</span>
                            <span className='text-primary detail-value'>{eventsItem.distance} KM</span>
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <button className="btn btn-hero-primary btn-sm w-100 pulse-glow">
                            <FaUsers className="me-2" />
                            {t('events.viewDetails')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </>
          ) : (
            <div className="text-center">
              <h3 className='text-white'>{t('events.error')}</h3>
            </div>
          )}
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="events-bg-decoration"></div>
    </section>
  );
};

export default Events;